import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocPortal, MapLocKeys } from '@cm/core/localization';

export type CPCertAgreementLocs =
	| 'CPCertAgreementLabelType'
	| 'CPCertAgreementLabelName'
	| 'CPCertAgreementLabelActiveDate'
	| 'CPCertAgreementLabelExpireDate'
	| 'CPCertAgreementContentNoExpiration'
	| 'CPCertAgreementButtonViewMore'
	| 'CPCertAgreementButtonInformation'
	| 'CPCertAgreementButtonLatestAgreement'
	| 'CPCertAgreementButtonSign'
	| 'CPCertAgreementContentSigned'
	| 'CPCertAgreementTitlePageTitle'
	| 'CPCertAgreementContentAgreementRequired'
	| 'CPCertAgreementDetailsFieldLabelDocumentation'
	| 'CPCertAgreementDetailsFieldErrorDocumentationRequired'
	| 'CPCertAgreementDetailsFieldErrorDocumentationSize'
	| 'CPCertAgreementDetailsFieldErrorDocumentationLimit'
	| 'CPCertAgreementDetailsFieldLabelDocumentationLanguage'
	| 'CPCertAgreementDetailsFieldErrorDocLanguageRequired'
	| 'CPCertAgreementDetailsFieldErrorDocLanguageInvalid'
	| 'CPCertAgreementDetailsFieldLabelProvider'
	| 'CPCertAgreementDetailsFieldLabelNotes'
	| 'CPCertAgreementDetailsButtonCancel'
	| 'CPCertAgreementDetailsButtonUpdate'
	| 'CPCertAgreementContentPageDescription';

export const cpCertAgreementLocs: LocDefs<CPCertAgreementLocs> = {
	CPCertAgreementLabelType: {
		defaultValue: 'Type',
		description: 'Label for type field',
		displayPath: 'Credential Agreement|Field Label|Type',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credential.Agreement.FieldLabel.Type',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementLabelName: {
		defaultValue: 'Name',
		description: 'Label for name field',
		displayPath: 'Credential Agreement|Field Label|Name',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credential.Agreement.FieldLabel.Name',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementLabelActiveDate: {
		defaultValue: 'Active Date',
		description: 'Label for active date field',
		displayPath: 'Credential Agreement|Field Label|Active Date',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credential.Agreement.FieldLabel.ActiveDate',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementLabelExpireDate: {
		defaultValue: 'Expiration Date',
		description: 'Label for expire date field',
		displayPath: 'Credential Agreement|Field Label|Expire Date',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credential.Agreement.FieldLabel.ExpireDate',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementContentNoExpiration: {
		defaultValue: 'Never',
		description: 'Content shown when there is no expiration date',
		displayPath: 'Credential Agreement|Content|No Expiration',
		displayType: 'Content',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credential.Agreement.Content.NoExpiration',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementButtonViewMore: {
		defaultValue: 'View More',
		description: 'Button text for view more action',
		displayPath: 'Credential Agreement|Button|View More',
		displayType: 'Button',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credential.Agreement.Button.ViewMore',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementButtonInformation: {
		defaultValue: 'Information',
		description: 'Button text for information action',
		displayPath: 'Credential Agreement|Button|Information',
		displayType: 'Button',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credential.Agreement.Button.Information',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementButtonLatestAgreement: {
		defaultValue: 'View Latest Agreement',
		description: 'Button text for latest agreement action',
		displayPath: 'Credential Agreement|Button|Latest Agreement',
		displayType: 'Button',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credentials.Agreement.Button.LatestAgreement',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementButtonSign: {
		defaultValue: 'Sign Latest Agreement',
		description: 'Button text for sign agreement action',
		displayPath: 'Credential Agreement|Button|Sign',
		displayType: 'Button',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credentials.Agreement.Button.Sign',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementContentSigned: {
		defaultValue: "You've already signed the latest agreement.",
		description: 'Content shown when agreement is already signed',
		displayPath: 'Credential Agreement|Content|Signed',
		displayType: 'Content',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credentials.Agreement.Content.Signed',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementTitlePageTitle: {
		defaultValue: 'Credential Agreement',
		description: 'Title for credential agreement page',
		displayPath: 'Credential Agreement|Title|Page Title',
		displayType: 'Title',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credentials.Agreement.Title.PageTitle',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementContentAgreementRequired: {
		defaultValue: 'You are required to sign the agreement before continuing to use this site.',
		description: 'Content shown when agreement is required',
		displayPath: 'Credential Agreement|Content|Agreement Required',
		displayType: 'Content',
		feature: HubFeature.CredentialAgreementAtLogin,
		legacyName: 'Credential.Agreement.AtLogin/System.Prerequisite.CertAgreementRequired',
		portal: 'CP' as LocPortal,
	},
	// New detail form localizations
	CPCertAgreementDetailsFieldLabelDocumentation: {
		defaultValue: 'Documentation',
		description: 'Label for documentation field in detail form',
		displayPath: 'Credential Agreement|Details|Field Label|Documentation',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'CertAgreement.CertAgreement.Details.FieldLabel.Documentation',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementDetailsFieldErrorDocumentationRequired: {
		defaultValue: 'Documentation is required',
		description: 'Error message when documentation is required but not provided',
		displayPath: 'Credential Agreement|Details|Field Error|Documentation Required',
		displayType: 'Error',
		feature: HubFeature.CredentialAgreement,
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementDetailsFieldErrorDocumentationSize: {
		defaultValue: 'Files must not be larger than 2Mb',
		description: 'Error message when documentation file size exceeds limit',
		displayPath: 'Credential Agreement|Details|Field Error|Documentation Size',
		displayType: 'Error',
		feature: HubFeature.CredentialAgreement,
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementDetailsFieldErrorDocumentationLimit: {
		defaultValue: 'Maximum of 5 files',
		description: 'Error message when documentation file count exceeds limit',
		displayPath: 'Credential Agreement|Details|Field Error|Documentation Limit',
		displayType: 'Error',
		feature: HubFeature.CredentialAgreement,
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementDetailsFieldLabelDocumentationLanguage: {
		defaultValue: 'Documentation Language',
		description: 'Label for documentation language field',
		displayPath: 'Credential Agreement|Details|Field Label|Documentation Language',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementDetailsFieldErrorDocLanguageRequired: {
		defaultValue: 'Documentation language is required',
		description: 'Error message when documentation language is required but not selected',
		displayPath: 'Credential Agreement|Details|Field Error|Doc Language Required',
		displayType: 'Error',
		feature: HubFeature.CredentialAgreement,
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementDetailsFieldErrorDocLanguageInvalid: {
		defaultValue: 'You must choose an option from the list',
		description: 'Error message when documentation language selection is invalid',
		displayPath: 'Credential Agreement|Details|Field Error|Doc Language Invalid',
		displayType: 'Error',
		feature: HubFeature.CredentialAgreement,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldError.DocLanguageInvalid',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementDetailsFieldLabelProvider: {
		defaultValue: 'Provider',
		description: 'Label for provider field',
		displayPath: 'Credential Agreement|Details|Field Label|Provider',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.Provider',
		portal: 'CP' as LocPortal,
	},

	CPCertAgreementDetailsFieldLabelNotes: {
		defaultValue: 'Notes',
		description: 'Label for notes field',
		displayPath: 'Credential Agreement|Details|Field Label|Notes',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.Notes',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementDetailsButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Button text for cancel action in details form',
		displayPath: 'Credential Agreement|Details|Button|Cancel',
		displayType: 'Button',
		feature: HubFeature.CredentialAgreement,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.Button.Cancel',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementDetailsButtonUpdate: {
		defaultValue: 'Save Changes',
		description: 'Button text for update action in details form',
		displayPath: 'Credential Agreement|Details|Button|Update',
		displayType: 'Button',
		feature: HubFeature.CredentialAgreement,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.Button.Update',
		portal: 'CP' as LocPortal,
	},
	CPCertAgreementContentPageDescription: {
		defaultValue: '',
		description: 'Page description for credential agreement',
		displayPath: 'Credential Agreement|Content|Page Description',
		displayType: 'Content',
		feature: HubFeature.CredentialAgreement,
		legacyName: 'Credential.Agreement/Credentials.Agreement.Content.PageDescription',
		portal: 'CP',
		htmlAllowed: true,
		shared: false,
	},
};

export const cpCertAgreementLocKeys = MapLocKeys<CPCertAgreementLocs>(cpCertAgreementLocs);
export type CPCertAgreementLocKeys = typeof cpCertAgreementLocKeys;
